import { useEffect, useRef } from "react"
import { useDataContext, FloorPlanMode } from "../../hooks/context"
import { MapKeys } from "./utils"
import { fetchFileDownloadUrl } from "../../hooks/network"
import { loadFloorPlanImage } from "@ciq/shared/pages/map/utils/loadFloorPlanImage"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"
import { ErrorToast } from "@ciq/shared/utils/toast"

/**
 * Manages a Mapbox image source and raster layer for floor plan overlay.
 * When pixel processing is enabled (feature flag), processes floor plan images
 * through a canvas pipeline to make white areas transparent with a halo effect
 * around dark lines. Falls back to raw image URL when processing is disabled
 * or CORS prevents canvas access.
 *
 * @param {Object} props
 * @param {Object} props.map - Mapbox GL map instance
 * @param {boolean} props.isMapLoading - Whether map is still loading
 * @param {boolean} props.showFloorPlan - Whether floor plan toggle is enabled
 */
export function FloorPlanLayer({ map, isMapLoading, showFloorPlan }) {
  const { floorPlan, schoolId } = useDataContext()
  const layerAddedRef = useRef(false)
  const opacityRef = useRef(floorPlan.opacity)
  const blobUrlRef = useRef(null)
  const { isFeatureEnabled } = useAnalyticsContext()
  const pixelProcessingEnabled = isFeatureEnabled(FeatureFlags.floor_plan_pixel_processing)

  const { mode, selectedFileId, files, opacity, coordinates } = floorPlan
  opacityRef.current = opacity
  const selectedFile = files.find(f => f.id === selectedFileId)
  const isActive =
    showFloorPlan &&
    selectedFile &&
    (mode === FloorPlanMode.POSITIONING || mode === FloorPlanMode.ASSIGNED)

  // Add/remove image source and layer
  useEffect(() => {
    if (!map || isMapLoading) return
    const controller = new AbortController()

    if (isActive && selectedFile && coordinates?.length === 4) {
      fetchFileDownloadUrl(schoolId, selectedFile.id, controller.signal)
        .then(async imageUrl => {
          if (controller.signal.aborted) return
          if (!imageUrl) {
            // Clear stale floor plan when selected file has no renderable image
            if (layerAddedRef.current) {
              if (map.getLayer(MapKeys.FLOOR_PLAN_LAYER)) map.removeLayer(MapKeys.FLOOR_PLAN_LAYER)
              if (map.getSource(MapKeys.FLOOR_PLAN_SOURCE_ID)) map.removeSource(MapKeys.FLOOR_PLAN_SOURCE_ID)
              layerAddedRef.current = false
              if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current)
                blobUrlRef.current = null
              }
            }
            return
          }

          let finalUrl = imageUrl
          let isBlobUrl = false

          if (pixelProcessingEnabled) {
            const result = await loadFloorPlanImage(imageUrl, controller.signal)
            finalUrl = result.url
            isBlobUrl = result.isBlobUrl
          }

          if (controller.signal.aborted) {
            if (isBlobUrl) URL.revokeObjectURL(finalUrl)
            return
          }

          // Revoke previous blob URL if any
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current)
          }
          blobUrlRef.current = isBlobUrl ? finalUrl : null

          if (map.getSource(MapKeys.FLOOR_PLAN_SOURCE_ID)) {
            map.getSource(MapKeys.FLOOR_PLAN_SOURCE_ID).updateImage({ url: finalUrl, coordinates })
          } else {
            map.addSource(MapKeys.FLOOR_PLAN_SOURCE_ID, {
              type: "image",
              url: finalUrl,
              coordinates,
            })
            // Insert floor plan above zone fills, below outlines/APs
            // Guard: only pass beforeId if the outline layer exists
            const beforeId = map.getLayer(MapKeys.CURRENT_ZONE_OUTLINE_LAYER)
              ? MapKeys.CURRENT_ZONE_OUTLINE_LAYER
              : undefined
            map.addLayer(
              {
                id: MapKeys.FLOOR_PLAN_LAYER,
                type: "raster",
                source: MapKeys.FLOOR_PLAN_SOURCE_ID,
                paint: { "raster-opacity": opacityRef.current / 100 },
              },
              beforeId
            )
            layerAddedRef.current = true
          }
        })
        .catch(e => {
          if (!controller.signal.aborted) {
            console.error("Failed to load floor plan image:", e)
            ErrorToast("Failed to load floor plan image")
          }
        })
    } else if (layerAddedRef.current) {
      if (map.getLayer(MapKeys.FLOOR_PLAN_LAYER)) map.removeLayer(MapKeys.FLOOR_PLAN_LAYER)
      if (map.getSource(MapKeys.FLOOR_PLAN_SOURCE_ID)) map.removeSource(MapKeys.FLOOR_PLAN_SOURCE_ID)
      layerAddedRef.current = false
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }

    return () => {
      controller.abort()
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- opacity handled by separate effect; track file by ID
  }, [map, isMapLoading, isActive, selectedFile?.id, coordinates, schoolId, pixelProcessingEnabled])

  // Update opacity
  useEffect(() => {
    if (!map || !layerAddedRef.current) return
    if (map.getLayer(MapKeys.FLOOR_PLAN_LAYER)) {
      map.setPaintProperty(MapKeys.FLOOR_PLAN_LAYER, "raster-opacity", opacity / 100)
    }
  }, [map, opacity])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return
      if (map.getLayer(MapKeys.FLOOR_PLAN_LAYER)) map.removeLayer(MapKeys.FLOOR_PLAN_LAYER)
      if (map.getSource(MapKeys.FLOOR_PLAN_SOURCE_ID)) map.removeSource(MapKeys.FLOOR_PLAN_SOURCE_ID)
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [map])

  return null
}
